<template>
  <div class="rtls-calendar-picker">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      min-width="290px"
    >
      <template #activator="{ props: activatorProps }">
        <v-text-field
          v-model="dateFormatted"
          hide-details
          :disabled="disabled"
          :loading="loading"
          :class="boldText ? 'bold-text' : ''"
          v-bind="activatorProps"
          style="min-width: 180px"
          @update:model-value="updateDateText"
        >
          <template #append-inner>
            <v-icon size="small" :title="iconTitle" v-bind="activatorProps">
              mdi-calendar-blank-outline
            </v-icon>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        hide-header
        :model-value="castDate"
        scrollable
        :allowed-dates="adjustedAllowedDates"
        @update:model-value="updateDatePicker"
        @input="menu = false"
      />
    </v-menu>
  </div>
</template>

<script>
import { isArray, debounce } from 'lodash';
import { useDayjs } from '@/composables/use-dayjs';

export const DateFormats = {
  shortDate: 'YYYY-MM-DD',
  humanDateEu: 'DD-MM-YYYY',
  ISO8601: 'YYYY-MM-DDTHH:mm:ss.msZ', // .toISOString used on saving of dates
};

const { ISO8601, humanDateEu: displayFormat, shortDate: parseFormat } = DateFormats;

export default {
  props: {
    value: {
      required: false,
      type: String,
      default: '',
    },
    boldText: {
      required: false,
      type: Boolean,
      default: false,
    },
    offsetX: {
      required: false,
      type: Boolean,
      default: false,
    },
    offsetY: {
      required: false,
      type: Boolean,
      default: true,
    },
    allowedDates: {
      required: false,
      type: [Function, Array],
      default: null,
    },
    firstDayOfWeek: {
      required: false,
      type: Number,
      default: 1, // Monday
    },
    displayDateFormat: {
      required: false,
      type: String,
      default: displayFormat,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    iconTitle: {
      required: false,
      type: String,
      default: 'Date Picker',
    },
  },

  emits: ['input'],

  setup() {
    const { dayjs, getTzZeroedDate } = useDayjs();

    return { dayjs, getTzZeroedDate };
  },

  data() {
    return {
      menu: null,
      dateFormatted: '',
      isTyping: false,
      expectedFormats: [ISO8601, displayFormat, parseFormat],
    };
  },

  computed: {
    castDate() {
      // We have to use a JS date here and that converts from YYYY-MM-DD to a local TZ, losing a day.
      // To avoid that, we use this function.
      return this.getTzZeroedDate(this.value);
    },

    adjustedAllowedDates() {
      if (isArray(this.allowedDates)) return this.allowedDates.map(this.getTzZeroedDate);

      if (typeof this.allowedDates === 'function') {
        // Ensure the date passed through to allowedDates is a string to avoid timezone issues.
        return date =>
          this.allowedDates(date instanceof Date ? this.dayjs(date).format(parseFormat) : date);
      }

      return this.allowedDates;
    },

    date: {
      get() {
        return this.isTyping || !this.isValidDate(this.value) ? null : this.value;
      },

      set(value) {
        this.$emit('input', value);
      },
    },

    calculateOffsetY() {
      return !this.offsetX && this.offsetY;
    },

    calculateOffsetX() {
      return this.offsetX;
    },
  },

  watch: {
    date() {
      if (!this.isTyping && this.isValidDate(this.date)) {
        this.dateFormatted = this.formatDate(this.date);
      }
    },

    value() {
      this.setTypingInactive();
    },
  },

  created() {
    if (this.value && this.isValidDate(this.value)) {
      this.dateFormatted = this.formatDate(this.value);
    }
    this.setTypingInactive = debounce(this.setTypingInactive, 2000);
  },

  methods: {
    formatDate(date) {
      if (!date) {
        return null;
      }

      return this.dayjs.utc(date).format(this.displayDateFormat);
    },

    parseDate(date) {
      if (!date) {
        return null;
      }
      return this.dayjs(date).format(parseFormat);
    },

    updateDatePicker(date) {
      if (this.isValidDate(date)) {
        this.dateFormatted = this.formatDate(date);
        this.$emit('input', this.parseDate(date));
      }
    },

    updateDateText(date) {
      if (this.isValidDate(date)) {
        const parsedDate = this.parseDate(date);
        this.$emit('input', parsedDate);
        this.setTypingActive();
      }
    },

    setTypingActive() {
      this.isTyping = true;
    },

    setTypingInactive() {
      this.isTyping = false;
    },

    isValidDate(date) {
      // last argument true means that it will strictly tested
      return this.dayjs(date).isValid();
    },
  },
};
</script>
